<template>
  <v-card>
    <v-toolbar dense>
      <v-toolbar-title>
        Pending Procedures
      </v-toolbar-title>
      <v-spacer />
      <remote-select
        v-model="userId"
        :placeholder="`Filter by ${searchPerms.text}`"
        :filter="{
          perm: { $in: searchPerms.perms },
          invisible: { $ne: true },
        }"
        service="users"
        class="pt-0"
        style="max-width: 200px;"
        clearable
        hide-details
        dense
      />
      <v-tooltip top v-if="perm === 'admin'">
        <v-btn flat icon slot="activator" @click.stop="showAll = !showAll">
          <v-icon>fal fa-user{{showAll ? '' : 's'}}</v-icon>
        </v-btn>
        <span>
          {{showAll ? 'Only show my supervised procedures' : 'Show for all supervisors'}}
        </span>
      </v-tooltip>
    </v-toolbar>
    <v-progress-linear
      v-show="loading"
      indeterminate
      class="my-0"
    />
    <div v-show="!loading" style="height: 7px;"></div>
    <v-list three-line>
      <v-list-tile
        v-if="perm === 'student' && ($route.params.id || $vuetify.breakpoint.smAndDown)"
        :to="{params: {id: 'new'}}"
        @click="$emit('update:moblist', false)"
      >
        <v-list-tile-action>
          <v-icon>far fa-plus</v-icon>
        </v-list-tile-action>
        <v-list-tile-title>
          New Clinical Session
        </v-list-tile-title>
      </v-list-tile>
      <v-list-tile
        v-for="att in attempts"
        :key="att._id"
        :to="{params: {id: att._id}}"
      >
        <v-list-tile-content :class="limitWarning(att.time)">
          <v-list-tile-title class="text-capitalize">
            {{
              proceduresFind({ query: { _id: { $in: att.proIds} } }).data
                .map(p=>p.name).join(', ')
            }}
          </v-list-tile-title>
          <v-list-tile-sub-title
            v-if="perm === 'student'"
          >
            {{ (usersGet(att.supervisorId) || {}).text }}
          </v-list-tile-sub-title>
          <v-list-tile-sub-title
            v-else-if="perm === 'staff' || (perm === 'admin' && att.supervisorId === user._id)"
          >
            {{ (usersGet(att.studentId) || {}).text }}
          </v-list-tile-sub-title>
          <v-list-tile-sub-title
            v-else
          >
            Student: {{ (usersGet(att.studentId) || {}).text }}<br>
            Supervisor: {{ (usersGet(att.supervisorId) || {}).text }}
          </v-list-tile-sub-title>
          <v-list-tile-sub-title>
            Expected completion: {{
              (new Date(att.createdAt || 0)).getTime() + att.duration*60*1000 | moment('from')
            }} ({{
              (new Date(att.createdAt || 0)).getTime() + att.duration*60*1000 | moment('HH:MM')
            }})
          </v-list-tile-sub-title>
        </v-list-tile-content>
        <v-list-tile-action>
          <v-list-tile-action-text  :class="limitWarning(att.time)">
            {{ att.createdAt || 0 | moment('from') }}
          </v-list-tile-action-text>
        </v-list-tile-action>
      </v-list-tile>
      <v-list-tile v-if="
        !attempts.length
        && (perm !== 'student' || !$route.params.id)
        && $vuetify.breakpoint.mdAndUp
      ">
        <v-list-tile-title>No un-graded clinical sessions found</v-list-tile-title>
      </v-list-tile>
    </v-list>
    <v-card-actions v-if="totalPages > 1" class="justify-center">
      <v-pagination
        v-model="page"
        :key="$route.params.id"
        :length="totalPages"
        style="display: contents;"
      />
    </v-card-actions>
  </v-card>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import debouncePromise from 'debounce-promise';
import uniq from 'lodash/uniq';
import RemoteSelect from '../../../components/input/remote-select.vue';

export default {
  components: {
    RemoteSelect,
  },
  props: {
    moblist: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      userId: null,
      showAll: false,
      attempts: [],
      page: 1,
      limit: 10,
      total: 0,
      loading: true,
      loadAttemptsDebounced: debouncePromise(this.loadAttempts, 200),
    };
  },
  computed: {
    ...mapState('auth', ['user']),
    ...mapGetters('users', { usersGet: 'get', usersFind: 'find' }),
    ...mapGetters('procedures', { proceduresFind: 'find' }),
    perm() { return this.user && this.user.perm; },
    totalPages() {
      return Math.ceil(this.total / this.limit);
    },
    searchPerms() {
      if (this.perm === 'student') return { perms: ['staff', 'admin'], text: 'Supervisor' };
      if (this.perm === 'staff') {
        return { perms: ['student'], text: 'Student' };
      }
      return { perms: ['admin', 'staff', 'student'], text: 'User' };
    },
    key() {
      return `${this.page}-${this.showAll}-${this.userId}`;
    },
    selectedUserPerm() {
      if (!this.userId) return null;
      return (this.usersGet(this.userId) || {}).perm;
    },
  },
  mounted() {
    this.loadAttempts();
  },
  watch: {
    showAll(to, from) {
      if (to === from) return;
      this.page = 1;
    },
    userId(to, from) {
      if (to === from) return;
      this.page = 1;
    },
    key(to, from) {
      if (to === from) return;
      this.loading = true;
      this.loadAttemptsDebounced();
    },
  },
  methods: {
    async loadMissingUsers(data, field) {
      const userIds = uniq(data.map(att => att[field]));
      const { data: existing } = this.usersFind({ query: { _id: { $in: userIds } } });
      const missingIds = userIds.filter(id => !existing.some(u => u._id === id));
      console.log(userIds, missingIds);
      if (missingIds.length) {
        await this.$store.dispatch('users/find', { query: { _id: { $in: missingIds }, $limit: missingIds.length } });
      }
    },
    async loadAttempts(force) {
      const gt = Date.now() - 30 * 24 * 60 * 60 * 1000;
      const query = {
        stat: 'inprogress',
        time: { $gte: new Date(gt) },
        $sort: { time: -1 },
        $limit: this.limit,
        $skip: (this.page - 1) * this.limit,
        ...(this.selectedUserPerm === 'student' ? { studentId: this.userId } : {}),
        ...(this.userId && this.selectedUserPerm !== 'student' ? { supervisorId: this.userId } : {}),
      };
      if (this.perm !== 'admin' || !this.userId) {
        if (this.perm === 'staff' || (this.perm === 'admin' && !this.showAll)) {
          query.supervisorId = this.user._id;
        }
        if (this.perm === 'student') {
          query.studentId = this.user._id;
        }
      }
      this.loading = true;
      const res = await this.$cacheFind.find('clinic-list', 'attempts', this.key, query, force);

      if (!query.studentId) {
        await this.$cacheFind.loadMissing('users', res.data, 'studentId');
      }
      if (!query.supervisorId) {
        await this.$cacheFind.loadMissing('users', res.data, 'supervisorId');
      }
      await this.$cacheFind.loadMissing('procedures', res.data, 'proIds');

      this.attempts = res.data;
      this.total = res.total;
      this.loading = false;
    },
    limitWarning(t) {
      const time = (new Date(t)).getTime();
      const day = 24 * 60 * 60 * 1000;
      if (time < Date.now() - 20 * day) return 'error--text';
      if (time < Date.now() - 10 * day) return 'warning--text';
      return '';
    },
  },
};
</script>
